import { useState, useMemo, useCallback, useEffect } from "react";
import axios from "axios";
import { ContactForm } from "../../ContactForm/ContactForm";
import "./PageLayout.css";
import { DetailSidebar } from "../DetailSidebar/DetailSidebar";
import { ListingGrid } from "../ListingGrid/ListingGrid";
import {
  BaseLayoutKey,
  CardLayout,
  ListingPageLayoutProps,
} from "../../../types/ui-types";
import { TabsMap } from "../../Tabs/types";
import { TabLayout } from "../../Tabs/TabLayout";
import { useEditMode } from "../../../hooks/useEditMode";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { fetchItemTypes } from "../../../store/slices/uiSlice";
import {
  fetchListings,
  selectListingsWithDetails,
  updateListing,
} from "../../../store/slices/listingsSlice";
import { ItemType, ListingWithDetails } from "../../../types";
import ListingForm from "../../Form/ListingForm";
import { LocationMap } from "../../../store/slices/locationsSlice";

export const LayoutMember = ({
  listing,
  location,
  tabs,
  hasContactForm = false,
}: ListingPageLayoutProps) => {
  // STATE
  const [showContactForm, setShowContactForm] = useState(false);
  const [showingForm, setShowingForm] = useState<ItemType | null>(null);
  const [sourceTabKey, setSourceTabKey] = useState<string | null>(null);
  const [editingListing, setEditingListing] = useState<ListingWithDetails | null>(null);

  // REDUX
  const dispatch = useAppDispatch();
  const itemTypes = useAppSelector((state) => state.ui.itemTypes);
  const allListings = useAppSelector(selectListingsWithDetails);
  const locations = useAppSelector((state) => state.locations?.byId);

  // Derived data for ListingForm
  const members = useMemo(() => {
    return Object.values(allListings).filter((l) => l.itemType === "person");
  }, [allListings]);

  const locationsByGoogleId = useMemo(() => {
    const byPlaceId: LocationMap = {};
    Object.values(locations || {}).forEach((location) => {
      if (location.placeId) {
        byPlaceId[location.placeId] = location;
      }
    });
    return byPlaceId;
  }, [locations]);

  // EDIT MODE
  const { isEditMode } = useEditMode({
    listingId: listing.id,
  });

  useEffect(() => {
    if (isEditMode && itemTypes.length === 0) {
      dispatch(fetchItemTypes());
    }
  }, [isEditMode, itemTypes.length, dispatch]);

  const handleItemTypeSelect = useCallback(
    (itemType: ItemType, tabKey: string) => {
      setShowingForm(itemType);
      setSourceTabKey(tabKey);
    },
    []
  );

  const handleFormClose = useCallback(() => {
    setShowingForm(null);
    setSourceTabKey(null);
    setEditingListing(null);
  }, []);

  const handleEditListing = useCallback((listingToEdit: ListingWithDetails) => {
    setEditingListing(listingToEdit);
  }, []);

  const handleFormSubmit = useCallback(
    async (formData: any) => {
      try {
        // No 'listed' array - items added from member page aren't submitted to a network
        const response = await axios.post("/api/listing", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        });

        handleFormClose();
        alert("Listing created successfully!");

        // Refresh listings to show the new item
        dispatch(fetchListings());

        return response.data;
      } catch (err) {
        console.error("Error submitting listing:", err);
        alert("Failed to create listing. Please try again.");
        throw err;
      }
    },
    [handleFormClose, dispatch]
  );

  const handleEditSubmit = useCallback(
    async (formData: FormData) => {
      if (!editingListing) return;

      try {
        const updateData: Record<string, any> = {};
        formData.forEach((value, key) => {
          updateData[key] = value;
        });

        await dispatch(
          updateListing({
            listingId: editingListing.id,
            data: updateData,
          })
        ).unwrap();

        handleFormClose();
        alert("Changes saved successfully!");

        // Refresh listings to show the updates
        dispatch(fetchListings());

        return updateData;
      } catch (err) {
        console.error("Error updating listing:", err);
        alert("Failed to save changes. Please try again.");
        throw err;
      }
    },
    [editingListing, handleFormClose, dispatch]
  );

  // VARS
  const cardLayout = cardLayoutByBaseLayout(listing.baseLayout);

  const TABS_WITH_ADD = ["making"];

  const tabsComponents: TabsMap = useMemo(() => {
    const tabEntries: TabsMap = Object.entries(tabs)
      .map(([key, connections]) => {
        const listings = connections.map((x) => x.listing);
        return {
          key: key,
          content: (
            <ListingGrid
              listings={listings}
              layoutType={cardLayout}
              isEditMode={isEditMode}
              onEditListing={handleEditListing}
            />
          ),
          canAdd: TABS_WITH_ADD.includes(key.toLowerCase()),
        };
      })
      .reduce((acc, curr) => {
        return {
          ...acc,
          [curr.key]: {
            label: curr.key,
            content: curr.content,
            canAdd: curr.canAdd,
          },
        };
      }, {});

    return tabEntries;
  }, [tabs, isEditMode, handleEditListing]);

  return (
    <div className="listing-wrapper">
      {/* Cover Image */}
      {listing.coverImgUrl && (
        <div className="listing-cover-image">
          <img src={listing.coverImgUrl} alt={`${listing.name} cover`} />
        </div>
      )}

      <div className="listing-detail-container">
        {/* Sidebar */}
        <div className="layout__sidebar">
          <DetailSidebar
            listing={listing}
            location={location}
            hasContactForm={hasContactForm}
          />
        </div>

        {/* Main Content Area */}
        <div className="layout__main-content">
          <main className="listing-content">
            <TabLayout
              tabs={tabsComponents}
              isEditMode={isEditMode}
              itemTypes={itemTypes}
              onItemTypeSelect={handleItemTypeSelect}
            />
          </main>
        </div>
      </div>

      {showContactForm && (
        <ContactForm
          recipientName={listing.name}
          recipientId={listing.id}
          onClose={() => setShowContactForm(false)}
        />
      )}

      {showingForm && (
        <ListingForm
          onSubmit={handleFormSubmit}
          onClose={handleFormClose}
          itemType={showingForm}
          members={members}
          existingLocations={locationsByGoogleId}
        />
      )}

      {editingListing && (
        <ListingForm
          onSubmit={handleEditSubmit}
          onClose={handleFormClose}
          itemType={editingListing.itemType as ItemType}
          members={members}
          existingLocations={locationsByGoogleId}
          isEditing={true}
          initialData={{
            id: editingListing.id,
            name: editingListing.name,
            itemType: editingListing.itemType as ItemType,
            subType: editingListing.subType,
            description: editingListing.description,
            url: editingListing.url,
            email: editingListing.email,
            socialLinks: editingListing.socialLinks,
            imageUrl: editingListing.imageUrl,
            imageUrlOriginal: (editingListing as any).imageUrlOriginal,
            coverImageUrl: editingListing.coverImgUrl,
            coverImageUrlOriginal: (editingListing as any).coverImgUrlOriginal,
            startDate: editingListing.startDate,
            endDate: editingListing.endDate,
            dateLabel: editingListing.dateLabel,
            approximateDate: (editingListing as any).approximateDate,
            recurrenceDescription: (editingListing as any).recurrenceDescription,
          }}
        />
      )}
    </div>
  );
};

function cardLayoutByBaseLayout(
  baseLayout: BaseLayoutKey | undefined
): CardLayout {
  switch (baseLayout) {
    case "artist":
      return "artist";
    default:
      return "default";
  }
}
